import OCAuthSDK from '@openclassrooms/auth-sdk';
import { ClientApi, ClientLanguage } from '@openclassrooms/sdk-web';
import { configurationService } from '@openclassrooms/ui-web/configuration';
import { clientApi } from '@openclassrooms/data-connect-web/configuration';

import LightClientApi from '@openclassrooms/sdk';

import scopes from '../constants/scopes';

export type SdkInitOptions = {
  language: ClientLanguage;
  rootUrl: string;
  authorizeUrl: string;
  anonymous: {
    clientId: string;
    clientSecret: string;
  };
  logged: {
    clientId: string;
    clientSecret?: never;
  };
};

type ClientType = 'anonymous' | 'logged';

class Sdk {
  client: ClientApi | null;

  authClient: OCAuthSDK | null;

  lightClient: LightClientApi<APIEndpoints> | null;

  protected clientsConfiguration: Pick<SdkInitOptions, ClientType>;

  protected isUsingConnectedClient: boolean | null;

  constructor() {
    this.client = null;
    this.lightClient = null;
    this.authClient = null;
    this.isUsingConnectedClient = null;
    this.clientsConfiguration = {
      anonymous: {
        clientId: '',
        clientSecret: '',
      },
      logged: {
        clientId: '',
      },
    };
  }

  public init({ language, rootUrl, authorizeUrl, anonymous, logged }: SdkInitOptions) {
    this.authClient = new OCAuthSDK({
      clientId: anonymous.clientId,
      clientSecret: anonymous.clientSecret,
      rootUrl,
      authorizeUrl,
    });

    this.client = new ClientApi({
      language,
      rootUrl,
    });

    this.lightClient = new LightClientApi<APIEndpoints>({
      rootUrl,
      language,
    });

    this.clientsConfiguration = {
      anonymous,
      logged,
    };

    this.authClient.onNewAccessToken(({ accessToken }) => {
      if (this.client) {
        this.client.accessToken = accessToken;
      }

      if (this.lightClient) {
        this.lightClient.accessToken = accessToken;
      }
    });

    const prependCheckAccessTokenInterceptor = true;

    const checkAccessTokenInterceptor = async (requestData: any) => {
      if (!this.authClient) {
        return requestData;
      }

      await this.authClient.checkAccessTokenExpiration();

      return requestData;
    };

    this.client.addRequestInterceptor(
      checkAccessTokenInterceptor,
      prependCheckAccessTokenInterceptor
    );
    this.lightClient.addRequestInterceptor(
      checkAccessTokenInterceptor,
      prependCheckAccessTokenInterceptor
    );

    configurationService.useOpenClassroomsClientApi(this.client);
    clientApi.setOCClient(this.client);
    clientApi.setLightOCClient(this.lightClient);
  }

  public isInit(): boolean {
    return Boolean(this.client?.accessToken) && Boolean(this.authClient);
  }

  public isConnected(): boolean {
    return Boolean(this.client?.accessToken) && Boolean(this.isUsingConnectedClient);
  }

  protected setClient(clientType: ClientType) {
    if (!this.authClient || !this.client || !this.lightClient) {
      throw new Error(
        `[SDK] trying to switch to ${clientType} client but init() has not been called`
      );
    }

    this.authClient.setClientIdSecret(
      this.clientsConfiguration[clientType].clientId,
      this.clientsConfiguration[clientType].clientSecret
    );

    this.client.accessToken = this.authClient.oAuthToken.accessToken;
    this.lightClient.accessToken = this.authClient.oAuthToken.accessToken;
  }

  public async switchToAnonymousClient() {
    if (!this.authClient || !this.client) {
      throw new Error('[SDK] trying to switch to anonymous client but init() has not been called');
    }

    if (this.isUsingConnectedClient === false) {
      return;
    }

    if (this.isUsingConnectedClient) {
      this.authClient.setOauth2Tokens();
    }

    this.isUsingConnectedClient = false;

    this.setClient('anonymous');

    const isScopeUpToDate =
      this.authClient.oAuthToken.scopes.toString() === scopes.ANONYMOUS.toString();

    if (this.client.accessToken && isScopeUpToDate) {
      return;
    }

    await this.authClient.requestToken({
      grantType: 'client_credentials',
      scopes: scopes.ANONYMOUS,
    });
  }

  public switchToConnectedClient() {
    if (!this.authClient || !this.client) {
      throw new Error('[SDK] trying to switch to connected client but init() has not been called');
    }

    if (this.isUsingConnectedClient) {
      return;
    }

    this.isUsingConnectedClient = true;

    this.setClient('logged');
  }
}

export default new Sdk();
